import { z } from "zod";
import { defineEvent } from "./registry.js";
/**
 * Akaryakıt operasyonu olayları (Prompt 12).
 *
 * **Tek tek satış / okuma için olay YOKTUR.** Bir istasyon günde on binlerce `fuel_transactions` satırı üretir;
 * her satır için outbox kaydı yazmak relay'i ve olay kuyruğunu boğar. Bunun yerine PARTİ başına bir özet olayı
 * yazılır; ayrıntıyı isteyen istemci yetki denetimli okuma uçlarından çeker.
 *
 * Yük KİŞİSEL VERİ TAŞIMAZ (plaka, TTS kimliği, pompacı yok): kiracıdaki tüm oturumlara gidebilir (KVKK, AGENTS.md §3.14).
 */
export const FUEL_INGEST_KINDS = ["TANK_READINGS", "TRANSACTIONS", "TOTALIZERS"];
export const fuelIngestKindSchema = z.enum(FUEL_INGEST_KINDS);
export const FUEL_INGEST_KIND_LABELS = {
    TANK_READINGS: "Tank okumaları",
    TRANSACTIONS: "Pompa satışları",
    TOTALIZERS: "Tabanca endeksleri",
};
export const fuelIngestBatchReceivedV1 = defineEvent({
    type: "fuel.ingest.batch_received",
    version: 1,
    description: "Toplu veri alımı partisi işlendi (kabul / yinelenen / reddedilen sayıları)",
    realtime: ["station"],
    payload: z.object({
        stationId: z.uuid(),
        kind: fuelIngestKindSchema,
        received: z.number().int().nonnegative(),
        accepted: z.number().int().nonnegative(),
        duplicates: z.number().int().nonnegative(),
        rejected: z.number().int().nonnegative(),
        /** Reddedilen kayıtların hata kodları ve sayıları — alarm / izleme için (kayıt ayrıntısı YOK). */
        rejectedCodes: z.record(z.string(), z.number().int().positive()),
        /**
         * Kabul edilen kayıtların dokunduğu iş günleri (Prompt 22 — raporlama projeksiyonu bu kovaları yeniden hesaplar).
         * EK alan, isteğe bağlı: eski olaylarda yoktur (tüketici o zaman bugünü + dünü yeniden hesaplar).
         */
        businessDates: z.array(z.iso.date()).max(62).optional(),
    }),
});
/** Dolum satırının fark özeti — olay yükünde KİŞİSEL VERİ taşınmaz (şoför adı, plaka yok). */
const deliveryLineVarianceSchema = z.object({
    productId: z.uuid(),
    documentL: z.string(),
    measuredL: z.string(),
    differenceL: z.string(),
    differencePercent: z.string(),
    toleranceL: z.string(),
    withinTolerance: z.boolean(),
});
export const fuelDeliveryConfirmedV1 = defineEvent({
    type: "fuel.delivery.confirmed",
    version: 1,
    description: "Tanker dolumu onaylandı: ölçülen miktar stoklara girdi",
    realtime: ["station"],
    payload: z.object({
        deliveryId: z.uuid(),
        companyId: z.uuid(),
        stationId: z.uuid(),
        deliveryNo: z.string(),
        orderId: z.uuid().nullable(),
        businessDate: z.iso.date(),
        distributorCounterpartyId: z.uuid(),
        /** Toplam ölçülen litre (tüm satırlar). */
        measuredL: z.string(),
        costStatus: z.enum(["NONE", "PROVISIONAL", "FINAL"]),
        withinTolerance: z.boolean().nullable(),
        lines: z.array(deliveryLineVarianceSchema),
    }),
});
export const fuelDeliveryVarianceExceededV1 = defineEvent({
    type: "fuel.delivery.variance_exceeded",
    version: 1,
    description: "Dolum farkı toleransı aştı ve itiraz edildi (alarm modülü — Prompt 20)",
    realtime: ["station"],
    payload: z.object({
        deliveryId: z.uuid(),
        companyId: z.uuid(),
        stationId: z.uuid(),
        deliveryNo: z.string(),
        despatchNo: z.string().nullable(),
        distributorCounterpartyId: z.uuid(),
        compareBasis: z.enum(["AMBIENT", "REFERENCE_15C"]),
        lines: z.array(deliveryLineVarianceSchema),
        /** İtiraz tutanağı PDF'inin dosya kimliği (yetki denetimli indirilir). */
        disputeFileId: z.uuid().nullable(),
    }),
});
export const fuelCostRecalculatedV1 = defineEvent({
    type: "fuel.cost.recalculated",
    version: 1,
    description: "Geriye dönük fatura eşleşmesi sonrası stok maliyeti geçmişi yeniden oynatıldı",
    realtime: ["tenant"],
    payload: z.object({
        stockCostId: z.uuid(),
        companyId: z.uuid(),
        stationId: z.uuid().nullable(),
        productId: z.uuid(),
        fromSeq: z.number().int().nonnegative(),
        replayedMovements: z.number().int().nonnegative(),
        previousAverage: z.string(),
        newAverage: z.string(),
    }),
});
export const fuelReconciliationClassifiedV1 = defineEvent({
    type: "fuel.reconciliation.classified",
    version: 1,
    description: "Islak stok mutabakatı NORMAL dışı sınıflandı (alarm modülü — Prompt 20)",
    realtime: ["station"],
    payload: z.object({
        reconciliationId: z.uuid(),
        companyId: z.uuid(),
        stationId: z.uuid(),
        tankId: z.uuid(),
        productId: z.uuid(),
        businessDate: z.iso.date(),
        classification: z.enum(["WATCH", "SUSPICIOUS", "CRITICAL"]),
        varianceL: z.string(),
        variancePercent: z.string().nullable(),
        cumulativeVarianceL: z.string(),
        findings: z.array(z.object({
            rule: z.string(),
            severity: z.enum(["WATCH", "SUSPICIOUS", "CRITICAL"]),
            measured: z.string(),
            threshold: z.string(),
        })),
    }),
});
export const fuelReconciliationApprovedV1 = defineEvent({
    type: "fuel.reconciliation.approved",
    version: 1,
    description: "Mutabakat onaylandı; fark fire (ya da sayım fazlası) olarak stoğa işlendi",
    realtime: ["station"],
    payload: z.object({
        reconciliationId: z.uuid(),
        companyId: z.uuid(),
        stationId: z.uuid(),
        tankId: z.uuid(),
        productId: z.uuid(),
        businessDate: z.iso.date(),
        normalLossL: z.string(),
        abnormalLossL: z.string(),
        surplusL: z.string(),
        lossAmount: z.string().nullable(),
        ledgerStatus: z.enum(["NONE", "POSTED", "QUEUED"]),
    }),
});
export const fuelPriceStaleSaleDetectedV1 = defineEvent({
    type: "fuel.price.stale_sale_detected",
    version: 1,
    description: "Fiyat yürürlüğe girdikten sonra ESKİ fiyattan satış yapıldı (pompa otomasyonu fiyatı almamış) — tabanca + değişim başına bir kez",
    realtime: ["station"],
    payload: z.object({
        stationId: z.uuid(),
        nozzleId: z.uuid(),
        productId: z.uuid(),
        snapshotId: z.uuid(),
        effectiveAt: z.iso.datetime({ offset: true }),
        expectedUnitPrice: z.string(),
        actualUnitPrice: z.string(),
        /** İhlali İLK yapan işlemin anı. Plaka / pompacı gibi kişisel veri TAŞINMAZ (KVKK). */
        firstTransactionAt: z.iso.datetime({ offset: true }),
    }),
});
export const fuelTotalizerAnomalyDetectedV1 = defineEvent({
    type: "fuel.totalizer.anomaly_detected",
    version: 1,
    description: "Tabanca endeksinde süreklilik anomalisi (azalma / aşırı sıçrama) — tabanca + gün başına bir kez",
    realtime: ["station"],
    payload: z.object({
        stationId: z.uuid(),
        nozzleId: z.uuid(),
        businessDate: z.iso.date(),
        reason: z.enum(["DECREASED", "JUMP_TOO_LARGE"]),
        previousL: z.string(),
        currentL: z.string(),
    }),
});
//# sourceMappingURL=fuel.v1.js.map